import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { FormProvider, UseFormSetError, useForm } from 'react-hook-form'
import { Trans, useTranslation } from 'react-i18next'
import { Link, useLocation, useNavigate } from 'react-router-dom'

import { zodResolver } from '@hookform/resolvers/zod'
import { isAxiosError } from 'axios'
import { getYear } from 'date-fns'

import styles from './ReservationNewFrame.module.scss'

import {
  Button,
  ErrorCard,
  FormNote,
  Loader,
  MainColumn,
  MultiColumn,
  RegistrationForm,
  RegistrationNewHead,
  RegistrationStepper,
  ReservationLayout,
  ReservationPanel,
  SideColumn,
  Text,
} from '../../components'
import { logCustomEvent } from '../../components/contents/Braze/brazeClient'
import {
  DEFAULT_TRANSACTION_ID,
  PATH,
  REGISTRATION_PATH,
} from '../../constants'
import {
  useAuthProvider,
  useCreateNextPath,
  useCreateTransactionApi,
  useCreditCardState,
  useGMOMultiPaymentApi,
  useLogin,
  useOptions,
  usePriceApi,
  usePropertyProvider,
  useRegistrationState,
  useReservationsRecentApi,
  useSearchQueryParams,
  useTransactionApi,
  useUpdateTransactionInputApi,
} from '../../hooks'
import { Registration, makeRegistrationSchema } from '../../schemas'
import {
  canonicalizeHyphen,
  getCurrentJstDateTime,
  isInvalidInvoiceAccessKeyError,
  isInvoicePayment,
  requiresPayment,
} from '../../utils'

const translateNamespace = 'frames.RegistrationNewFrame'

type UseHandleSubmit = {
  setLoading: (value: boolean) => void
  setError: UseFormSetError<Registration>
  setPageError: (value: boolean) => void
  isReady: boolean
  isCardRequired: boolean
  isInvoiceBilledPayment: boolean
  transactionId?: number
  showSpecialServiceRequest?: boolean
  showCompanyName?: boolean
  showPurposeOfUse?: boolean
  showCheckInTime?: boolean
}

const useSetError = (setError: UseFormSetError<Registration>) => {
  const { t } = useTranslation()
  const onError = useCallback(
    (error: string) => {
      switch (error) {
        case '100': // Cardno error
        case '101': // Card format error
        case '102': // Card range error
          setError('cardNumber', {
            message: t(`${translateNamespace}.error.cardno`),
          })
          break
        case '110':
        case '111':
        case '112':
          setError('expirationYear', {
            message: t(`${translateNamespace}.error.expirationYear`),
          })
          break
        case '113':
          setError('expirationMonth', {
            message: t(`${translateNamespace}.error.expirationMonth`),
          })
          break
        case '121': // CVC error
        case '122': // CVC range error
          setError('securityCode', {
            message: t(`${translateNamespace}.error.securityCode`),
          })
          break
        case '131': // Holder name
        case '132': // Holder name range error
          setError('holderName', {
            message: t(`${translateNamespace}.error.holderName`),
          })
          break
        default:
          break
      }
    },
    [setError, t]
  )

  return { onError }
}

const useHandleSubmit = ({
  setLoading,
  setError,
  setPageError,
  isReady,
  isCardRequired,
  isInvoiceBilledPayment,
  transactionId,
  showSpecialServiceRequest,
  showCompanyName,
  showPurposeOfUse,
  showCheckInTime,
}: UseHandleSubmit) => {
  const { token } = useAuthProvider()
  const navigate = useNavigate()
  const { onUpdateRegistrationState } = useRegistrationState()
  const { onUpdateCreditCardState } = useCreditCardState()
  const { trigger: updateTransaction } = useUpdateTransactionInputApi(token)
  const { onGetToken } = useGMOMultiPaymentApi()
  const { onCreateNextPath } = useCreateNextPath()
  const { onError } = useSetError(setError)

  const onHandleSubmit = useCallback(
    async (formValues: Registration) => {
      if (!isReady) {
        return
      }
      setLoading(true)

      try {
        // On-site (0円) plans skip GMO tokenization entirely. For card-required
        // plans we tokenize the card, guard the result code, and stash the
        // resulting token fields so the only difference between the two paths
        // is the presence of gmoToken / gmoTokenExpiredAt in the input below.
        let card:
          | {
              cardno: string
              expirationMonth: string
              expirationYear: string
              holderName: string
            }
          | undefined
        let gmoFields:
          | { gmoToken: string; gmoTokenExpiredAt: string }
          | undefined
        if (isCardRequired) {
          const cardInput = {
            cardno: canonicalizeHyphen(formValues.cardNumber),
            expire: `${formValues.expirationYear}${formValues.expirationMonth}`,
            holdername: formValues.holderName,
            securitycode: formValues.securityCode,
          }
          const { resultCode, tokenObject } = await onGetToken(cardInput)
          if (!resultCode) {
            return
          }
          if (resultCode.toString() !== '000') {
            setLoading(false)
            // TODO Error handling
            onError(resultCode.toString())
            return
          }
          card = {
            cardno: cardInput.cardno,
            expirationMonth: formValues.expirationMonth,
            expirationYear: formValues.expirationYear,
            holderName: cardInput.holdername,
          }
          gmoFields = {
            gmoToken: tokenObject.token[0],
            gmoTokenExpiredAt: tokenObject.toBeExpiredAt,
          }
        }

        if (!transactionId) {
          return
        }

        // Save personal information to localstorage. Pass ONLY the personal
        // fields: react-hook-form retains values for unmounted inputs, so on
        // the on-site path formValues can still carry a stale/partial card
        // number or security code. useRegistrationState persists via @hookstate
        // merge (which writes any extra keys it receives), so handing it the raw
        // formValues would leak card data into cleartext localStorage. The card
        // is saved separately (in memory) only when it is actually required.
        onUpdateRegistrationState({
          name: formValues.name,
          kana: formValues.kana,
          phone: formValues.phone,
          postalCode: formValues.postalCode,
          prefecture: formValues.prefecture,
          city: formValues.city,
          street: formValues.street,
          email: formValues.email,
          specialServiceRequest: formValues.specialServiceRequest,
          companyName: formValues.companyName,
          purposeOfUse: formValues.purposeOfUse,
          checkInTime: formValues.checkInTime,
          invoiceAddressee: formValues.invoiceAddressee,
          birthYear: formValues.birthYear,
          birthMonth: formValues.birthMonth,
          birthDay: formValues.birthDay,
        })
        // Save card number, expire dates, holder name to in memory.
        if (card) {
          onUpdateCreditCardState(card)
        }

        const res = await updateTransaction({
          transactionId,
          input: {
            address: `${formValues.postalCode} ${formValues.prefecture} ${formValues.city} ${formValues.street}`,
            email: formValues.email,
            ...(gmoFields ?? {}),
            kana: formValues.kana,
            name: formValues.name,
            phone: formValues.phone,
            postalCode: formValues.postalCode,
            // specialServiceRequest predates this PR, so existing properties
            // that omit the new BE flag still receive the user's input.
            specialServiceRequest:
              showSpecialServiceRequest === false
                ? ''
                : formValues.specialServiceRequest,
            // The lounge fields are brand new — react-hook-form will still
            // submit default/localStorage values for unmounted inputs, so
            // gate them on explicit opt-in to avoid leaking previously
            // entered data into properties that did not enable the field.
            companyName:
              showCompanyName === true ? formValues.companyName || null : null,
            purposeOfUse:
              showPurposeOfUse === true
                ? formValues.purposeOfUse || null
                : null,
            checkInTime:
              showCheckInTime === true ? formValues.checkInTime || null : null,
            // Send the addressee only for invoice-billed plans: react-hook-form
            // keeps the localStorage-restored value even when the field is not
            // rendered, and other plans must not carry a stale addressee.
            ...(isInvoiceBilledPayment
              ? { invoiceAddressee: formValues.invoiceAddressee }
              : {}),
            dateOfBirth:
              formValues.birthYear !== '' &&
              formValues.birthMonth !== '' &&
              formValues.birthDay
                ? `${formValues.birthYear}-${formValues.birthMonth}-${formValues.birthDay}`
                : undefined,
          },
        })
        if (!res || res.status !== 204) {
          setPageError(true)
          window.scrollTo(0, 0)
          return
        }
        navigate(
          onCreateNextPath(REGISTRATION_PATH.confirmation, {
            queryParams: { transaction: transactionId },
          })
        )
      } catch (e) {
        setPageError(true)
        window.scrollTo(0, 0)
      } finally {
        setLoading(false)
      }
    },
    [
      setLoading,
      navigate,
      onCreateNextPath,
      onGetToken,
      onUpdateRegistrationState,
      onUpdateCreditCardState,
      updateTransaction,
      onError,
      setPageError,
      isReady,
      isCardRequired,
      isInvoiceBilledPayment,
      transactionId,
      showSpecialServiceRequest,
      showCompanyName,
      showPurposeOfUse,
      showCheckInTime,
    ]
  )

  return { onHandleSubmit }
}

const useDefaultValues = () => {
  const { registrationState } = useRegistrationState()
  const { creditCardState } = useCreditCardState()

  const currentDate = getCurrentJstDateTime()
  const year = getYear(currentDate)

  // Get personal information from fid(OpenID Connect) data.
  // Prefer user-entered values(saved at localstorage).
  const auth = useAuthProvider()
  const {
    name,
    kana,
    phone,
    postalCode,
    prefecture,
    city,
    street,
    email,
    specialServiceRequest,
    companyName,
    purposeOfUse,
    checkInTime,
    invoiceAddressee,
    birthYear,
    birthMonth,
    birthDay,
  } = registrationState

  return useMemo(() => {
    const [authBirthYear, authBirthMonth, authBirthDay] =
      auth.dateOfBirth.split('-')
    return {
      token: auth.token,
      defaultValues: {
        name: name || auth.name,
        kana: kana || auth.kana,
        phone: phone || auth.phone,
        postalCode: postalCode || auth.postalCode,
        prefecture: prefecture || auth.prefecture || '北海道',
        city: city || auth.address1,
        street: street || `${auth.address2} ${auth.address3}`,
        email: email || auth.email,
        cardNumber: creditCardState.cardno || '',
        holderName: creditCardState.holderName || '',
        expirationMonth: creditCardState.expirationMonth || '01',
        expirationYear: creditCardState.expirationYear || year.toString(),
        securityCode: '',
        specialServiceRequest: specialServiceRequest || '',
        companyName: companyName || '',
        purposeOfUse: purposeOfUse || '',
        checkInTime: checkInTime || '',
        invoiceAddressee: invoiceAddressee || '',
        birthYear: birthYear || authBirthYear || '1980',
        birthMonth: birthMonth || authBirthMonth || '01',
        birthDay: birthDay || authBirthDay || '01',
      },
    }
  }, [
    name,
    kana,
    phone,
    postalCode,
    prefecture,
    city,
    street,
    email,
    specialServiceRequest,
    companyName,
    purposeOfUse,
    checkInTime,
    invoiceAddressee,
    auth.name,
    auth.email,
    auth.address1,
    auth.address2,
    auth.address3,
    auth.kana,
    auth.phone,
    auth.postalCode,
    auth.prefecture,
    auth.token,
    auth.dateOfBirth,
    year,
    creditCardState.cardno,
    creditCardState.expirationMonth,
    creditCardState.expirationYear,
    creditCardState.holderName,
    birthYear,
    birthMonth,
    birthDay,
  ])
}

export const RegistrationNewFrame = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { params, onCreateNextPath } = useCreateNextPath()
  const { params: queryParams } = useSearchQueryParams()
  const { token, defaultValues } = useDefaultValues()
  const {
    isHotel,
    isSpa,
    isActivity,
    showSpecialServiceRequest,
    showCompanyName,
    showPurposeOfUse,
    showCheckInTime,
  } = usePropertyProvider()
  const { trigger: createTransaction } = useCreateTransactionApi(token)
  const [isPriceLoading, setPriceLoading] = useState(true)
  const [isInvoiceAccessKeyInvalid, setInvoiceAccessKeyInvalid] =
    useState(false)
  const { onLogin } = useLogin()
  const [isLoading, setLoading] = useState(false)
  const { onGet: onGetOptions } = useOptions()
  const options = onGetOptions()
  const [isPriceLoaded, setPriceLoaded] = useState(false)
  const [transactionId, setTransactionId] = useState<number | undefined>(
    queryParams.transaction
  )
  const { data: transaction, isLoading: isTransactionLoading } =
    useTransactionApi(transactionId, token)
  const isReserved = Number.isFinite(transaction?.reservationId)
  // Fetch the price so we can tell whether this is an on-site (現地決済)
  // plan, in which case the card form and GMO tokenization are skipped. The
  // SWR key is null until a real transactionId exists (see usePriceApi), so
  // priceData is undefined at first and isCardRequired safely defaults to true.
  // ReservationPanel calls the same hook with the same transactionId, so SWR
  // shares the cache (no double fetch).
  const { data: priceData } = usePriceApi({
    token,
    checkInDate: queryParams.checkInDate,
    checkOutDate: queryParams.checkOutDate,
    numberOfAdults: queryParams.numberOfAdults,
    numberOfChildren: queryParams.numberOfChildren,
    numberOfInfants: queryParams.numberOfInfants,
    propertySlug: params.property ?? '',
    roomSlug: params.room ?? '',
    planSlug: params.plan ?? '',
    roomCount: queryParams.roomCount,
    transactionId: transactionId ?? DEFAULT_TRANSACTION_ID,
  })
  const isCardRequired = requiresPayment(priceData?.paymentType)
  const isInvoiceBilledPayment = isInvoicePayment(priceData?.paymentType)
  const hasUrlTransaction =
    !!queryParams.transaction &&
    queryParams.transaction !== DEFAULT_TRANSACTION_ID
  // Hold the URL coupon back until an EXISTING (URL-provided) transaction's
  // reserved-state is known, so we never auto-apply onto a finalized
  // transaction during the window where useTransactionApi is still loading
  // and isReserved is therefore still false. A transaction created this
  // session (no URL id) is definitely not reserved, so it is not held back.
  const autoApplyDiscountCode =
    isReserved || (hasUrlTransaction && isTransactionLoading)
      ? ''
      : queryParams.discountCode

  // Synchronous gate for the one-render window opened when a URL transaction
  // resolves as not-reserved: in that very render isTransactionLoading flips to
  // false (so the loading gate below lifts synchronously) while autoApplyDiscountCode
  // transitions empty -> non-empty, i.e. we have only just handed the coupon to
  // the panel. The panel retracts its price-loaded signal from an effect (post
  // commit), so without this gate isReady would briefly be true on the stale
  // isPriceLoaded from the pre-release render, letting the guest confirm before
  // the coupon is applied. We hold isReady false from the release render until
  // the panel emits its next onPriceDidLoad reflecting the released state.
  //
  // Deadlock-free by construction: the gate clears on the FIRST onPriceDidLoad
  // after release (any value), and the panel always re-emits onPriceDidLoad
  // once auto-apply settles (success, failure, or invalid code all reach
  // .finally -> isAutoApplying=false and autoAppliedRef -> willAutoApply=false;
  // see ReservationPanel). If data never arrives the panel stays on <Loader />,
  // so submit is unreachable regardless of this gate.
  //
  // The pending flag is STATE, not a ref: clearing it in onPriceDidLoad must
  // re-evaluate isReady even when isPriceLoaded does not change (the panel may
  // re-emit the same loaded value), so the clear has to trigger a render itself.
  // It is set during render via the standard "setState while rendering" pattern,
  // guarded by prevAutoApplyDiscountCodeRef so it only fires on the empty ->
  // non-empty transition and cannot loop.
  const prevAutoApplyDiscountCodeRef = useRef('')
  const [isCouponReleasePending, setCouponReleasePending] = useState(false)
  if (
    !prevAutoApplyDiscountCodeRef.current &&
    autoApplyDiscountCode &&
    !isCouponReleasePending
  ) {
    setCouponReleasePending(true)
  }
  prevAutoApplyDiscountCodeRef.current = autoApplyDiscountCode

  // While an EXISTING (URL-provided) transaction's reserved-state is still
  // loading, keep submission pending: otherwise the panel reports the price
  // loaded on the pre-discount, not-yet-known-reserved state and the confirm
  // button could enable before the coupon is applied/revalidated.
  const isReady =
    isPriceLoaded &&
    !(hasUrlTransaction && isTransactionLoading) &&
    !isCouponReleasePending

  useEffect(() => {
    logCustomEvent('fota_add_to_cart', {
      property: params.property,
      room: params.room,
      plan: params.plan,
    })
  }, [params.plan, params.property, params.room])

  const ref = useRef(true)
  useEffect(() => {
    // To avoid multiple executions
    if (!ref.current || transactionId !== DEFAULT_TRANSACTION_ID) {
      setPriceLoading(false)

      // Update transaction ID in query parameter
      const path = onCreateNextPath(PATH.registrations.root, {
        queryParams: { transaction: transactionId },
      })
      window.history.replaceState(null, '', `${path.pathname}?${path.search}`)

      return
    }
    ref.current = false
    // At first, call transaction api to create transaction.
    // (It's like server-side session)
    // When guest click confirm button, call transaction input api to save
    // guest inputted values.
    createTransaction({
      bookingEngineHotel: params.property,
      bookingEnginePlan: params.plan,
      bookingEngineRoom: params.room,
      checkInDate: queryParams.checkInDate,
      checkOutDate: isHotel
        ? queryParams.checkOutDate
        : queryParams.checkInDate,
      numberOfAdults: queryParams.numberOfAdults,
      numberOfChildren: queryParams.numberOfChildren,
      numberOfInfants: queryParams.numberOfInfants,
      roomCount: queryParams.roomCount,
      options,
      // Present only on the plan's invoice URL; the plan is then invoice-billed
      // instead of paid by card (SB-3056).
      ...(queryParams.invoiceKey
        ? { invoiceAccessKey: queryParams.invoiceKey }
        : {}),
    })
      .then((res) => {
        if (res?.status === 401) {
          onLogin()
          return
        }
        // If transaction was created then call price api.
        // If price called before transaction created, returns 404 error.
        setPriceLoading(false)
        setTransactionId(res?.data.id)
      })
      .catch((e) => {
        if (isAxiosError(e) && e.response?.status === 401) {
          onLogin()
          return
        }
        // The guest expects invoice billing and must not be led into card
        // payment by a key that does not open this plan.
        if (isInvalidInvoiceAccessKeyError(e)) {
          setInvoiceAccessKeyInvalid(true)
        }
      })
  }, [
    createTransaction,
    params.property,
    params.room,
    params.plan,
    queryParams.checkInDate,
    queryParams.checkOutDate,
    queryParams.numberOfAdults,
    queryParams.numberOfChildren,
    queryParams.numberOfInfants,
    queryParams.roomCount,
    queryParams.invoiceKey,
    onLogin,
    options,
    transactionId,
    onCreateNextPath,
    isHotel,
  ])

  const formMethods = useForm<Registration>({
    defaultValues,
    resolver: zodResolver(
      makeRegistrationSchema({
        requiresPayment: isCardRequired,
        isInvoicePayment: isInvoiceBilledPayment,
      })
    ),
  })
  const {
    handleSubmit,
    formState: { errors },
    setError,
    clearErrors,
  } = formMethods
  const isError = Object.keys(errors).length > 0

  // When the loaded price turns out to be an on-site (現地決済) plan, the card
  // inputs unmount. If the guest had already triggered validation while the
  // card form was still showing (e.g. pressing Enter before the price loaded),
  // their card errors would otherwise linger in `errors`, keeping `isError`
  // true and the confirm button disabled with no visible field to fix. Clear
  // the card-field errors once the card is no longer required.
  useEffect(() => {
    if (!isCardRequired) {
      clearErrors([
        'cardNumber',
        'holderName',
        'expirationMonth',
        'expirationYear',
        'securityCode',
      ])
    }
  }, [isCardRequired, clearErrors])

  // Same reasoning for the invoice addressee: once the plan is known not to be
  // invoice-billed, its input is gone, so a lingering error must not keep the
  // confirm button disabled.
  useEffect(() => {
    if (!isInvoiceBilledPayment) {
      clearErrors('invoiceAddressee')
    }
  }, [isInvoiceBilledPayment, clearErrors])

  const [isPageError, setPageError] = useState(false)

  const [existsRecent, setExistsRecent] = useState(false)
  const { data } = useReservationsRecentApi({
    token,
    isHotel: isHotel ?? false,
    isSpa: isSpa ?? false,
    isActivity: isActivity ?? false,
  })
  useEffect(() => {
    if ((data?.count ?? 0) > 0) {
      setExistsRecent(true)
    }
  }, [data])

  const { onHandleSubmit } = useHandleSubmit({
    setLoading,
    setError,
    setPageError,
    isReady,
    isCardRequired,
    isInvoiceBilledPayment,
    transactionId,
    showSpecialServiceRequest,
    showCompanyName,
    showPurposeOfUse,
    showCheckInTime,
  })
  const onSubmit = handleSubmit(onHandleSubmit)

  const reservationPath = useMemo(() => {
    if (isSpa) {
      return PATH.reservations.spas
    }
    if (isActivity) {
      return PATH.reservations.activities
    }
    return PATH.reservations.hotels
  }, [isSpa, isActivity])

  const location = useLocation()
  const isIcePark = location.pathname.includes('/ice-park')

  if (isLoading) {
    return (
      <Loader>
        <Text align="center">
          <Trans i18nKey={`${translateNamespace}.loading`} />
        </Text>
      </Loader>
    )
  }

  if (isInvoiceAccessKeyInvalid) {
    return (
      <ReservationLayout pageTitle={t(`${translateNamespace}.pageTitle`)}>
        <RegistrationStepper
          current={1}
          stepName={t(`${translateNamespace}.stepper`)}
        />
        <ErrorCard>
          <Text>
            {t(`${translateNamespace}.error.invalidInvoiceAccessKey`)}
          </Text>
        </ErrorCard>
      </ReservationLayout>
    )
  }

  return (
    <ReservationLayout pageTitle={t(`${translateNamespace}.pageTitle`)}>
      <RegistrationStepper
        current={1}
        stepName={t(`${translateNamespace}.stepper`)}
      />
      <FormProvider {...formMethods}>
        <MultiColumn isSpColumnReverse>
          <MainColumn>
            {isPageError && (
              <ErrorCard>
                <Text>
                  <Trans
                    i18nKey={`${translateNamespace}.error.confirmationError`}
                  >
                    Sorry, An error has occurred We apologize for the
                    inconvenience.
                    <br />
                    Please change your search conditions and try again.
                  </Trans>
                </Text>
              </ErrorCard>
            )}
            {isReserved && transaction?.reservationId && (
              <ErrorCard>
                <Text>
                  <Trans
                    i18nKey={`${translateNamespace}.reserved`}
                    t={t}
                    components={{
                      reservationLink: (
                        <Link
                          className={styles.link}
                          to={onCreateNextPath(reservationPath.reservation, {
                            pathParams: {
                              reservation: transaction.reservationId.toString(),
                            },
                            clearQueryParams: true,
                          })}
                        />
                      ),
                    }}
                  />
                </Text>
              </ErrorCard>
            )}
            {!isReserved && existsRecent && (
              <ErrorCard warning>
                <Text>
                  <Trans
                    i18nKey={`${translateNamespace}.error.exists`}
                    t={t}
                    components={{
                      reservationsLink: (
                        <Link
                          className={styles.link}
                          to={reservationPath.root}
                        />
                      ),
                    }}
                  />
                </Text>
              </ErrorCard>
            )}
            <FormNote>
              <Text>{t(`${translateNamespace}.note`)}</Text>
            </FormNote>
            <form onSubmit={onSubmit}>
              <RegistrationForm
                // showSpecialServiceRequest is optional in PropertyResponse:
                // BE may omit it during rollout. Fall back to the legacy
                // behavior unless the flag is explicitly false.
                disabledInquiryForm={showSpecialServiceRequest === false}
                enabledCodeForm={isIcePark}
                showCompanyName={showCompanyName}
                showPurposeOfUse={showPurposeOfUse}
                showCheckInTime={showCheckInTime}
                requiresPayment={isCardRequired}
                isInvoicePayment={isInvoiceBilledPayment}
              />
              <MultiColumn isSpColumnReverse>
                <Button
                  htmlType="button"
                  variant="tertiary"
                  outline
                  size="large"
                  onClick={() => {
                    if (options.length > 0) {
                      navigate(onCreateNextPath(PATH.options))
                      return
                    }
                    // Plan-first flow (no options): return to the fixed-plan
                    // room page rather than the public room-scoped plan list,
                    // which a secret/plan-first URL should never expose
                    // (Codex P2 / 3359901212). PlanRoomItem tags this nav with a
                    // `from_plan_rooms` query param (not router state) so the
                    // marker survives login redirects / reloads (3360001710).
                    if (queryParams.fromPlanRooms) {
                      navigate(onCreateNextPath(PATH.plan.rooms))
                      return
                    }
                    navigate(onCreateNextPath(PATH.plans))
                  }}
                >
                  {(() => {
                    // Mirror the Back destinations above so the label always
                    // matches where the button actually goes: options →
                    // "back to options", plan-first room page → "back to
                    // rooms", otherwise the public plan list → "back to plans".
                    if (options.length > 0) {
                      return t(`${translateNamespace}.backToOption`)
                    }
                    if (queryParams.fromPlanRooms) {
                      return t(`${translateNamespace}.backToRooms`)
                    }
                    return t(`${translateNamespace}.backToPlan`)
                  })()}
                </Button>
                <Button
                  htmlType="submit"
                  variant="primary"
                  size="large"
                  disabled={isError || !isReady || isReserved}
                >
                  {t(`${translateNamespace}.confirm`)}
                </Button>
              </MultiColumn>
            </form>
          </MainColumn>
          <SideColumn>
            {isPriceLoading ? (
              <Loader />
            ) : (
              <ReservationPanel
                isHotel={isHotel}
                showDiscountCode={!isReserved}
                autoApplyDiscountCode={autoApplyDiscountCode}
                checkInDate={queryParams.checkInDate}
                checkOutDate={queryParams.checkOutDate}
                numberOfAdults={queryParams.numberOfAdults}
                numberOfChildren={queryParams.numberOfChildren}
                numberOfInfants={queryParams.numberOfInfants}
                roomCount={queryParams.roomCount}
                property={params.property}
                room={params.room}
                plan={params.plan}
                transactionId={transactionId}
                onApiError={(_error) => {
                  setPageError(true)
                  window.scrollTo(0, 0)
                }}
                onPriceDidLoad={(loaded) => {
                  // The panel has reported back after we (possibly) released the
                  // URL coupon, so lift the release gate. Clearing on the FIRST
                  // callback after release (any value) is what keeps the gate
                  // deadlock-free; using a state setter guarantees isReady
                  // re-evaluates even if isPriceLoaded itself is unchanged.
                  setCouponReleasePending(false)
                  setPriceLoaded(loaded)
                }}
              />
            )}
          </SideColumn>
        </MultiColumn>
      </FormProvider>
      <RegistrationNewHead transactionId={transactionId} />
    </ReservationLayout>
  )
}
